import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { FinanceApiService } from '../finance/finance-api.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateInsuranceEndorsementPayload,
  CreateInsurancePremiumSchedulePayload,
  CreateInsurancePolicyPayload,
  InsurancePolicyCoverage,
  InsurancePremiumInvoiceListItem,
  InsurancePremiumSchedule,
  CreateInsuranceRenewalOfferPayload,
  InsuranceAuditLogItem,
  InsuranceEndorsement,
  InsuranceApiService,
  InsuranceSanhabSubmission,
  InsuranceMemberEligibility,
  InsurancePolicyDocument,
  InsurancePolicyDraftRenderResult,
  InsurancePolicyListItem,
  InsurancePolicyPartyRole,
  InsurancePolicyTerm,
  InsuranceCoverageItem,
  InsuranceProductListItem,
  InsuranceRatingPlan,
  InsuranceRenewalOffer,
  RenewInsurancePolicyPayload,
  RunInsurancePremiumEarningPayload,
  UpsertInsuranceMemberEligibilityPayload,
  UpsertInsurancePolicyCoveragesPayload,
  UpsertInsurancePolicyPartyRolesPayload,
  UpdateInsurancePolicyPayload
} from './insurance-api.service';
import { InsuranceAiCopilotPanelComponent } from './insurance-ai-copilot-panel.component';
import { InsurancePolicyDraftPreviewComponent } from './insurance-policy-draft-preview.component';
import { getEndorsementDeltaFields } from './insurance-endorsement-delta-schemas';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  defaultStructuredJsonModel,
  serializeStructuredJsonPayload
} from '../../shared/design-system/structured-json-form.utils';
import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';

type PolicyTab = 'summary' | 'coveragesParties' | 'eligibility' | 'endorsements' | 'renewals' | 'premium' | 'billing' | 'claims' | 'documents' | 'audit' | 'regulatory';

@Component({
  standalone: true,
  selector: 'ain-insurance-policy-detail',
  imports: [
    AinAmountPipe,
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CheckboxModule,
    PartySelectorComponent,
    CurrencySelectorComponent,
    JalaliDateInputComponent,
    InfoHintComponent,
    ButtonModule,
    DialogModule,
    TableModule,
    InsuranceAiCopilotPanelComponent,
    InsurancePolicyDraftPreviewComponent,
    StructuredJsonFormComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.policyDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.policies.help.detail.title'"
        [descriptionKey]="'insurance.policies.help.detail.description'"
        [bulletKeys]="[
          'insurance.policies.help.detail.bullets.lifecycle',
          'insurance.policies.help.detail.bullets.tabs',
          'insurance.policies.help.detail.bullets.billing'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (isCreateMode()) {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/policies']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving() || !createCanSave()" (click)="saveCreate()"></button>
        </div>
        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.policies.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.policies.help.info.title'"
              [descriptionKey]="'insurance.policies.help.info.description'"
              [bulletKeys]="[
                'insurance.policies.help.info.bullets.product',
                'insurance.policies.help.info.bullets.premium',
                'insurance.policies.help.info.bullets.dates'
              ]" />
          </div>
          <form class="grid" (ngSubmit)="saveCreate()">
            <label>
              <span>{{ t('insurance.policies.fields.legalEntityId') }}</span>
              <ain-party-selector [(ngModel)]="createForm.legalEntityId" name="legalEntityId" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.policyNumber') }}</span>
              <input pInputText [(ngModel)]="createForm.policyNumber" name="policyNumber" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.product') }}</span>
              <p-select
                [options]="productOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="selectedProductId"
                (ngModelChange)="onCreateProductChange($event)"
                name="productId"
                [showClear]="true"
                [filter]="true"
                appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.lineOfBusiness') }}</span>
              <input pInputText [(ngModel)]="createForm.lineOfBusiness" name="lineOfBusiness" readonly />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.status') }}</span>
              <p-select [options]="policyStatusOptions" optionValue="value" [(ngModel)]="createForm.status" name="status" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.premium') }}</span>
              <p-inputNumber [(ngModel)]="createForm.premiumAmount" name="premiumAmount" [min]="0" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.currencyCode') }}</span>
              <ain-currency-selector [(ngModel)]="createForm.currencyCode" name="currencyCode" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.effectiveFrom') }}</span>
              <ain-date-input [(ngModel)]="createForm.effectiveFromUtc" name="effectiveFromUtc" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.effectiveTo') }}</span>
              <ain-date-input [(ngModel)]="createForm.effectiveToUtc" name="effectiveToUtc" />
            </label>
          </form>
        </section>
        @if (error()) { <p class="error">{{ error() }}</p> }
      } @else if (!policy()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/policies']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            <button pButton type="button" severity="secondary" [label]="t('insurance.policyDetail.draftPreview.action')" (click)="openDraftPreview()"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving() || !canSaveEdit()" (click)="saveEdit()"></button>
          </div>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.policies.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.policies.help.info.title'"
              [descriptionKey]="'insurance.policies.help.info.description'"
              [bulletKeys]="[
                'insurance.policies.help.info.bullets.product',
                'insurance.policies.help.info.bullets.premium',
                'insurance.policies.help.info.bullets.dates'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.policies.fields.policyNumber') }}: <strong>{{ policy()!.policyNumber }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ t(statusKey(policy()!.status)) }}</strong></span>
            <span>{{ t('insurance.policies.fields.product') }}: <strong>{{ productDisplayLabel(policy()!.productName) }}</strong></span>
            <span>{{ t('insurance.policies.fields.premium') }}: <strong>{{ policy()!.premiumAmount | ainAmount }} {{ policy()!.currencyCode }}</strong></span>
          </div>
          <form class="grid" (ngSubmit)="saveEdit()">
            <label>
              <span>{{ t('insurance.policies.fields.product') }}</span>
              <p-select
                [options]="productOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="selectedEditProductId"
                (ngModelChange)="onEditProductChange($event)"
                name="editProductId"
                [showClear]="true"
                [filter]="true"
                appendTo="body" />
              @if (!selectedEditProductId && policy()!.productName) {
                <span class="field-hint">{{ productDisplayLabel(policy()!.productName) }}</span>
              }
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.lineOfBusiness') }}</span>
              <p-select
                [options]="lineOfBusinessOptions()"
                optionValue="value"
                [(ngModel)]="editForm.lineOfBusiness"
                name="editLineOfBusiness"
                [showClear]="true"
                [filter]="true"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? item.label : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ item.label }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.status') }}</span>
              <p-select [options]="policyStatusOptions" optionValue="value" [(ngModel)]="editForm.status" name="editStatus" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.premium') }}</span>
              <p-inputNumber [(ngModel)]="editForm.premiumAmount" name="editPremiumAmount" [min]="0" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.currencyCode') }}</span>
              <ain-currency-selector [(ngModel)]="editForm.currencyCode" name="editCurrencyCode" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.effectiveFrom') }}</span>
              <ain-date-input [(ngModel)]="editForm.effectiveFromUtc" name="editEffectiveFromUtc" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.effectiveTo') }}</span>
              <ain-date-input [(ngModel)]="editForm.effectiveToUtc" name="editEffectiveToUtc" />
            </label>
          </form>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.policies.sections.lifecycle') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.policies.help.lifecycle.title'"
              [descriptionKey]="'insurance.policies.help.lifecycle.description'"
              [bulletKeys]="[
                'insurance.policies.help.lifecycle.bullets.transitions',
                'insurance.policies.help.lifecycle.bullets.irreversible'
              ]" />
          </div>
          <div class="lifecycle-actions">
            <button pButton type="button" [label]="t('insurance.policyDetail.actions.issue')" [disabled]="policy()!.status !== 'Bound' || saving()" (click)="issue()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.policyDetail.actions.suspend')" [disabled]="policy()!.status !== 'InForce' || saving()" (click)="suspend()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.policyDetail.actions.reinstate')" [disabled]="policy()!.status !== 'Suspended' || saving()" (click)="reinstate()"></button>
            <button pButton type="button" severity="danger" [label]="t('insurance.policyDetail.actions.cancel')" [disabled]="(policy()!.status !== 'InForce' && policy()!.status !== 'Suspended') || saving()" (click)="cancel()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.policyDetail.actions.expire')" [disabled]="(policy()!.status !== 'InForce' && policy()!.status !== 'Suspended') || saving()" (click)="expire()"></button>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'summary'" (click)="activeTab.set('summary')">{{ t('insurance.policyDetail.tabs.summary') }}</button>
            <button type="button" [class.active]="activeTab() === 'coveragesParties'" (click)="activeTab.set('coveragesParties')">{{ t('insurance.policyDetail.tabs.coveragesParties') }}</button>
            <button type="button" [class.active]="activeTab() === 'eligibility'" (click)="activeTab.set('eligibility')">{{ t('insurance.policyDetail.tabs.eligibility') }}</button>
            <button type="button" [class.active]="activeTab() === 'endorsements'" (click)="activeTab.set('endorsements')">{{ t('insurance.policyDetail.tabs.endorsements') }}</button>
            <button type="button" [class.active]="activeTab() === 'renewals'" (click)="activeTab.set('renewals')">{{ t('insurance.policyDetail.tabs.renewals') }}</button>
            <button type="button" [class.active]="activeTab() === 'premium'" (click)="activeTab.set('premium')">{{ t('insurance.policyDetail.tabs.premium') }}</button>
            <button type="button" [class.active]="activeTab() === 'billing'" (click)="activeTab.set('billing')">{{ t('insurance.policyDetail.tabs.billing') }}</button>
            <button type="button" [class.active]="activeTab() === 'claims'" (click)="activeTab.set('claims')">{{ t('insurance.policyDetail.tabs.claims') }}</button>
            <button type="button" [class.active]="activeTab() === 'documents'" (click)="activeTab.set('documents')">{{ t('insurance.policyDetail.tabs.documents') }}</button>
            <button type="button" [class.active]="activeTab() === 'audit'" (click)="activeTab.set('audit')">{{ t('insurance.policyDetail.tabs.audit') }}</button>
            <button type="button" [class.active]="activeTab() === 'regulatory'" (click)="activeTab.set('regulatory')">{{ t('insurance.regulatory.console.policyRegulatory.title') }}</button>
          </div>

        @if (activeTab() === 'summary') {
          <div class="lines-header">
            <h4>{{ t('insurance.policyDetail.tabs.summary') }}</h4>
            <ain-info-hint
              [titleKey]="'insurance.policies.help.summary.title'"
              [descriptionKey]="'insurance.policies.help.summary.description'"
              [bulletKeys]="[
                'insurance.policies.help.summary.bullets.readonly',
                'insurance.policies.help.summary.bullets.terms'
              ]" />
          </div>
          <div class="summary-grid">
            <p><strong>{{ t('insurance.policies.fields.period') }}:</strong> {{ policy()!.effectiveFromUtc | ainDate }} - {{ policy()!.effectiveToUtc | ainDate }}</p>
            <p><strong>{{ t('insurance.policies.fields.product') }}:</strong> {{ productDisplayLabel(policy()!.productName) }}</p>
            <p><strong>{{ t('insurance.policies.fields.lineOfBusiness') }}:</strong> {{ lobLabel(policy()!.lineOfBusiness) }}</p>
            <p><strong>{{ t('insurance.policyDetail.fields.issueDate') }}:</strong> {{ policy()!.issueDateUtc ? (policy()!.issueDateUtc! | ainDate) : '-' }}</p>
            <p><strong>{{ t('insurance.policyDetail.fields.lastReason') }}:</strong> {{ policy()!.lastLifecycleReason || '-' }}</p>
            <div class="wide">
              <h4>{{ t('insurance.policyDetail.sections.terms') }}</h4>
              @if (terms().length === 0) {
                <p>{{ t('insurance.policyDetail.states.noTerms') }}</p>
              } @else {
                <ul>
                  @for (term of terms(); track term.id) {
                    <li>#{{ term.termNumber }} - {{ term.fromUtc | ainDate }} - {{ term.toUtc | ainDate }} ({{ term.premiumAmount | ainAmount }} {{ term.currencyCode }})</li>
                  }
                </ul>
              }
            </div>
          </div>
        } @else if (activeTab() === 'coveragesParties') {
            <div class="lines-header">
              <h4>{{ t('insurance.policyDetail.sections.coverages') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.policies.help.tabs.coverages.title'"
                  [descriptionKey]="'insurance.policies.help.tabs.coverages.description'"
                  [bulletKeys]="[
                    'insurance.policies.help.tabs.coverages.bullets.catalog',
                    'insurance.policies.help.tabs.coverages.bullets.snapshot',
                    'insurance.policies.help.tabs.coverages.bullets.deductible'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openCoverageCreate()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.save')" [disabled]="saving()" (click)="saveCoverages()"></button>
              </div>
            </div>
            @if (!hasCoverageCatalog()) {
              <p class="field-hint">{{ t('insurance.policies.help.tabs.coverages.noCatalog') }}</p>
            }
            @if (coverages().length === 0) {
              <p>{{ t('insurance.policyDetail.states.noCoverages') }}</p>
            } @else {
              <p-table [value]="coverages()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.policyDetail.coverages.code') }}</th>
                    <th>{{ t('insurance.policyDetail.coverages.name') }}</th>
                    <th>{{ t('insurance.policyDetail.coverages.sumInsured') }}</th>
                    <th>{{ t('insurance.policyDetail.coverages.premium') }}</th>
                    <th>{{ t('insurance.policyDetail.coverages.deductible') }}</th>
                    <th>{{ t('insurance.policyDetail.coverages.currency') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-i="rowIndex">
                  <tr>
                    <td>{{ row.coverageCode }}</td>
                    <td>{{ row.coverageName }}</td>
                    <td>{{ row.sumInsured | ainAmount }}</td>
                    <td>{{ row.premiumAmount | ainAmount }}</td>
                    <td>{{ row.deductibleAmount ?? '-' }}</td>
                    <td>{{ row.currencyCode }}</td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openCoverageEdit(i)"></button>
                      <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removeCoverage(i)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }

            <div class="lines-header section-gap">
              <h4>{{ t('insurance.policyDetail.sections.parties') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.policies.help.tabs.parties.title'"
                  [descriptionKey]="'insurance.policies.help.tabs.parties.description'"
                  [bulletKeys]="[
                    'insurance.policies.help.tabs.parties.bullets.roles',
                    'insurance.policies.help.tabs.parties.bullets.partyId'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openPartyCreate()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.save')" [disabled]="saving()" (click)="savePartyRoles()"></button>
              </div>
            </div>
            @if (partyRoles().length === 0) {
              <p>{{ t('insurance.policyDetail.states.noParties') }}</p>
            } @else {
              <p-table [value]="partyRoles()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.policyDetail.parties.partyId') }}</th>
                    <th>{{ t('insurance.policyDetail.parties.role') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-i="rowIndex">
                  <tr>
                    <td>{{ partyDisplayLabel(row.partyId) }}</td>
                    <td>{{ partyRoleLabel(row.role) }}</td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openPartyEdit(i)"></button>
                      <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removePartyRole(i)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
        } @else if (activeTab() === 'eligibility') {
            <div class="lines-header">
              <h4>{{ t('insurance.policyDetail.tabs.eligibility') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.policies.help.tabs.eligibility.title'"
                  [descriptionKey]="'insurance.policies.help.tabs.eligibility.description'"
                  [bulletKeys]="[
                    'insurance.policies.help.tabs.eligibility.bullets.member',
                    'insurance.policies.help.tabs.eligibility.bullets.planCode',
                    'insurance.policies.help.tabs.eligibility.bullets.waiting'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openEligibilityCreate()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.save')" [disabled]="saving()" (click)="saveEligibility()"></button>
              </div>
            </div>
            @if (eligibilities().length === 0) {
              <p>{{ t('insurance.policyDetail.eligibility.empty') }}</p>
            } @else {
              <p-table [value]="eligibilities()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.policyDetail.eligibility.memberPartyId') }}</th>
                    <th>{{ t('insurance.policyDetail.eligibility.planCode') }}</th>
                    <th>{{ t('insurance.policyDetail.eligibility.eligibleFrom') }}</th>
                    <th>{{ t('insurance.policyDetail.eligibility.eligibleTo') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-i="rowIndex">
                  <tr>
                    <td>{{ partyDisplayLabel(row.memberPartyId) }}</td>
                    <td>{{ row.planCode }}</td>
                    <td>{{ row.eligibleFrom | ainDate }}</td>
                    <td>{{ row.eligibleTo | ainDate }}</td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openEligibilityEdit(i)"></button>
                      <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removeEligibility(i)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
        } @else if (activeTab() === 'endorsements') {
            <div class="lines-header">
              <h4>{{ t('insurance.policyDetail.tabs.endorsements') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.policies.help.tabs.endorsements.title'"
                  [descriptionKey]="'insurance.policies.help.tabs.endorsements.description'"
                  [bulletKeys]="[
                    'insurance.policies.help.tabs.endorsements.bullets.draft',
                    'insurance.policies.help.tabs.endorsements.bullets.premium'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openEndorsementCreate()"></button>
              </div>
            </div>
            @if (endorsements().length === 0) {
              <p>{{ t('insurance.policyDetail.states.noEndorsements') }}</p>
            } @else {
              <p-table [value]="endorsements()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.policyDetail.endorsement.number') }}</th>
                    <th>{{ t('insurance.policyDetail.endorsement.type') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('insurance.policyDetail.endorsement.effectiveAt') }}</th>
                    <th>{{ t('insurance.policyDetail.endorsement.premiumAdjustment') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.endorsementNumber }}</td>
                    <td>{{ endorsementTypeLabel(row.type) }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.effectiveAtUtc | ainDate }}</td>
                    <td>{{ row.premiumAdjustment | ainAmount }} {{ policy()!.currencyCode }}</td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" [label]="t('insurance.policyDetail.endorsement.apply')" [disabled]="row.status !== 'Draft' || saving()" (click)="applyEndorsement(row.id)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
        } @else if (activeTab() === 'renewals') {
            <div class="lines-header">
              <h4>{{ t('insurance.policyDetail.tabs.renewals') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.policies.help.tabs.renewals.title'"
                  [descriptionKey]="'insurance.policies.help.tabs.renewals.description'"
                  [bulletKeys]="[
                    'insurance.policies.help.tabs.renewals.bullets.offer',
                    'insurance.policies.help.tabs.renewals.bullets.accept'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openRenewalCreate()"></button>
              </div>
            </div>
            @if (renewalOffers().length === 0) {
              <p>{{ t('insurance.policyDetail.states.noRenewals') }}</p>
            } @else {
              <p-table [value]="renewalOffers()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.policyDetail.renewal.offerNumber') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('insurance.policyDetail.renewal.offerValidUntil') }}</th>
                    <th>{{ t('insurance.policyDetail.renewal.premiumAmount') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.offerNumber }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.offerValidUntilUtc | ainDate }}</td>
                    <td>{{ row.premiumAmount | ainAmount }} {{ row.currencyCode }}</td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" [label]="t('insurance.policyDetail.renewal.acceptOffer')" [disabled]="row.status !== 'Active' || saving()" (click)="renewFromOffer(row.id)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
        } @else if (activeTab() === 'premium') {
            <div class="lines-header">
              <h4>{{ t('insurance.policyDetail.tabs.premium') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.policies.help.tabs.premium.title'"
                [descriptionKey]="'insurance.policies.help.tabs.premium.description'"
                [bulletKeys]="[
                  'insurance.policies.help.tabs.premium.bullets.readonly',
                  'insurance.policies.help.tabs.premium.bullets.rating'
                ]" />
            </div>
            <p>{{ t('insurance.policyDetail.premiumReadonly') }}</p>
            <div class="list-grid">
              <div class="row">
                <span>{{ t('insurance.policyDetail.premium.base') }}</span>
                <span>{{ policy()!.premiumAmount | ainAmount }} {{ policy()!.currencyCode }}</span>
              </div>
              <div class="row">
                <span>{{ t('insurance.policyDetail.premium.total') }}</span>
                <span>{{ policy()!.premiumAmount | ainAmount }} {{ policy()!.currencyCode }}</span>
              </div>
            </div>
            <a class="back-link" [routerLink]="['/insurance/rating']">{{ t('insurance.policyDetail.premium.ruleSourceLink') }}</a>
        } @else if (activeTab() === 'billing') {
            <div class="lines-header">
              <h4>{{ t('insurance.policyDetail.tabs.billing') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.policies.help.tabs.billing.title'"
                [descriptionKey]="'insurance.policies.help.tabs.billing.description'"
                [bulletKeys]="[
                  'insurance.policies.help.tabs.billing.bullets.schedule',
                  'insurance.policies.help.tabs.billing.bullets.invoices'
                ]" />
            </div>
            @if (billingPermissionDenied()) {
              <p>{{ t('insurance.states.noPermissionMessage') }}</p>
            } @else {
              <h4>{{ t('insurance.policyDetail.billing.scheduleTitle') }}</h4>
              @if (!premiumSchedule()) {
                <p>{{ t('insurance.policyDetail.billing.noSchedule') }}</p>
                <form class="grid" (ngSubmit)="createPremiumSchedule()">
                  <label>
                    <span>{{ t('insurance.policyDetail.billing.fields.installments') }}</span>
                    <p-inputNumber [(ngModel)]="scheduleForm.installmentCount" name="installmentCount" [min]="1" [max]="24" />
                  </label>
                  <label>
                    <span>{{ t('insurance.policyDetail.billing.fields.frequency') }}</span>
                    <p-select [options]="billingFrequencyOptions" optionValue="value" [(ngModel)]="scheduleForm.frequency" name="frequency" appendTo="body">
                      <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                      <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                    </p-select>
                  </label>
                  <label>
                    <span>{{ t('insurance.policyDetail.billing.fields.dunningDays') }}</span>
                    <p-inputNumber [(ngModel)]="scheduleForm.dunningSuspendAfterDays" name="dunningSuspendAfterDays" [min]="1" [max]="365" />
                  </label>
                  <div class="actions">
                    <button pButton type="submit" [label]="t('insurance.policyDetail.billing.actions.createSchedule')" [disabled]="saving()"></button>
                  </div>
                </form>
              } @else {
                <p>
                  {{ premiumSchedule()!.frequency }} / {{ premiumSchedule()!.installmentCount }}
                  ({{ t('insurance.policyDetail.billing.fields.dunningDays') }}: {{ premiumSchedule()!.dunningSuspendAfterDays ?? '-' }})
                </p>
                <ul>
                  @for (line of premiumSchedule()!.lines; track line.id) {
                    <li>{{ line.dueDateUtc | ainDate }} - {{ line.amount | ainAmount }} - {{ line.status }}</li>
                  }
                </ul>
              }

              <div class="actions">
                <button pButton type="button" severity="secondary" [label]="t('insurance.policyDetail.billing.actions.runEarning')" [disabled]="saving()" (click)="runEarning()"></button>
              </div>

              <h4>{{ t('insurance.policyDetail.billing.invoicesTitle') }}</h4>
              @if (premiumInvoices().length === 0) {
                <p>{{ t('insurance.policyDetail.billing.noInvoices') }}</p>
              } @else {
                <div class="list-grid">
                  @for (invoice of premiumInvoices(); track invoice.id) {
                    <div class="row">
                      <span>{{ invoice.invoiceNumber }} / {{ invoice.status }}</span>
                      <span>{{ invoice.outstandingAmount | ainAmount }}</span>
                      <button pButton type="button" size="small" [label]="t('insurance.policyDetail.billing.actions.openInvoice')" (click)="openPremiumInvoice(invoice.id)"></button>
                    </div>
                  }
                </div>
              }
            }
        } @else if (activeTab() === 'claims') {
            <div class="lines-header">
              <h4>{{ t('insurance.policyDetail.tabs.claims') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.policies.help.tabs.claims.title'"
                [descriptionKey]="'insurance.policies.help.tabs.claims.description'"
                [bulletKeys]="['insurance.policies.help.tabs.claims.bullets.stub']" />
            </div>
            <p>{{ t('insurance.policyDetail.claimsStub') }}</p>
        } @else if (activeTab() === 'documents') {
            <div class="lines-header">
              <h4>{{ t('insurance.policyDetail.tabs.documents') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.policies.help.tabs.documents.title'"
                [descriptionKey]="'insurance.policies.help.tabs.documents.description'"
                [bulletKeys]="['insurance.policies.help.tabs.documents.bullets.generated']" />
            </div>
            @if (documents().length === 0) {
              <p>{{ t('insurance.policyDetail.documentsStub') }}</p>
            } @else {
              <div class="list-grid">
                @for (item of documents(); track item.documentCode) {
                  <div class="row">
                    <span>{{ item.title }} ({{ item.documentType }})</span>
                    <span>{{ item.createdAtUtc | ainDate }}</span>
                  </div>
                }
              </div>
            }
        } @else if (activeTab() === 'audit') {
            <div class="lines-header">
              <h4>{{ t('insurance.policyDetail.tabs.audit') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.policies.help.tabs.audit.title'"
                [descriptionKey]="'insurance.policies.help.tabs.audit.description'"
                [bulletKeys]="['insurance.policies.help.tabs.audit.bullets.trace']" />
            </div>
            @if (auditLogs().length === 0) {
              <p>{{ t('insurance.policyDetail.auditStub') }}</p>
            } @else {
              <div class="list-grid">
                @for (item of auditLogs(); track item.id) {
                  <div class="row">
                    <span>{{ item.entityName }} / {{ item.action }}</span>
                    <span>{{ item.createdAtUtc | ainDate }}</span>
                  </div>
                }
              </div>
            }
        } @else {
            <div class="lines-header">
              <h4>{{ t('insurance.regulatory.console.policyRegulatory.title') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.policies.help.tabs.regulatory.title'"
                [descriptionKey]="'insurance.policies.help.tabs.regulatory.description'"
                [bulletKeys]="[
                  'insurance.policies.help.tabs.regulatory.bullets.sync',
                  'insurance.policies.help.tabs.regulatory.bullets.submission'
                ]" />
            </div>
            <p>{{ t('insurance.regulatory.console.policyRegulatory.description') }}</p>
            <div class="actions">
              <button pButton type="button" [label]="t('insurance.regulatory.console.actions.syncPolicy')" [disabled]="saving()" (click)="syncPolicyToSanhab()"></button>
            </div>
            @if (sanhabSubmission()) {
              <div class="list-grid">
                <div class="row"><span>{{ t('insurance.regulatory.console.fields.status') }}</span><span>{{ sanhabSubmission()!.status }}</span></div>
                <div class="row"><span>{{ t('insurance.regulatory.console.fields.externalReference') }}</span><span>{{ sanhabSubmission()!.externalReference }}</span></div>
                <div class="row"><span>{{ t('insurance.regulatory.console.fields.submittedAt') }}</span><span>{{ sanhabSubmission()!.createdAtUtc | ainDate }}</span></div>
                <div class="row"><span>Hash</span><span>{{ sanhabSubmission()!.requestPayloadHash }}</span></div>
              </div>
            } @else {
              <p>{{ t('insurance.regulatory.console.messages.noSubmission') }}</p>
            }
        }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }
        <ain-insurance-ai-copilot-panel entityType="InsurancePolicy" [entityId]="policy()!.id" />
        <ain-insurance-policy-draft-preview #draftPreview />

        <p-dialog [header]="coverageDialogTitle()" [modal]="true" [(visible)]="coverageDialogVisible" [style]="{ width: '720px' }">
          <div class="dialog-grid">
            <label>
              <span>{{ t('insurance.policyDetail.coverages.code') }}</span>
              @if (hasCoverageCatalog()) {
                <p-select [options]="coverageCodeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="coverageDraft.coverageCode" (ngModelChange)="onCoverageDraftCodeChanged($event)" [filter]="true" appendTo="body" />
              } @else {
                <input pInputText [(ngModel)]="coverageDraft.coverageCode" />
              }
            </label>
            <label><span>{{ t('insurance.policyDetail.coverages.name') }}</span><input pInputText [(ngModel)]="coverageDraft.coverageName" /></label>
            <label><span>{{ t('insurance.policyDetail.coverages.sumInsured') }}</span><p-inputNumber [(ngModel)]="coverageDraft.sumInsured" [min]="0" /></label>
            <label><span>{{ t('insurance.policyDetail.coverages.premium') }}</span><p-inputNumber [(ngModel)]="coverageDraft.premiumAmount" [min]="0" /></label>
            <label>
              <span>{{ t('insurance.policyDetail.coverages.deductible') }}</span>
              <p-inputNumber [(ngModel)]="coverageDraft.deductibleAmount" [min]="0" />
              <span class="field-hint">{{ t('insurance.policyDetail.coverages.deductibleHint') }}</span>
            </label>
            <label><span>{{ t('insurance.policyDetail.coverages.currency') }}</span><ain-currency-selector [(ngModel)]="coverageDraft.currencyCode" /></label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="coverageDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="saveCoverageDialog()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog [header]="partyDialogTitle()" [modal]="true" [(visible)]="partyDialogVisible" [style]="{ width: '560px' }">
          <div class="dialog-grid">
            <label><span>{{ t('insurance.policyDetail.parties.partyId') }}</span><ain-party-selector [(ngModel)]="partyDraft.partyId" /></label>
            <label>
              <span>{{ t('insurance.policyDetail.parties.role') }}</span>
              <p-select [options]="partyRoleOptionsFor(partyDraft.role)" optionValue="value" [(ngModel)]="partyDraft.role" appendTo="body">
                <ng-template pTemplate="selectedItem" let-option>{{ option ? partyRoleLabel(option.value) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-option>{{ partyRoleLabel(option.value) }}</ng-template>
              </p-select>
            </label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="partyDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="savePartyDialog()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog [header]="eligibilityDialogTitle()" [modal]="true" [(visible)]="eligibilityDialogVisible" [style]="{ width: '760px' }">
          <div class="dialog-grid">
            <label><span>{{ t('insurance.policyDetail.eligibility.memberPartyId') }}</span><ain-party-selector [(ngModel)]="eligibilityDraft.memberPartyId" /></label>
            <label><span>{{ t('insurance.policyDetail.eligibility.certificateId') }}</span><ain-party-selector [(ngModel)]="eligibilityDraft.certificateId" /></label>
            <label>
              <span>{{ t('insurance.policyDetail.eligibility.planCode') }}</span>
              <p-select [options]="healthPlanCodeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="eligibilityDraft.planCode" [filter]="true" appendTo="body" />
              <span class="field-hint">{{ t('insurance.policyDetail.eligibility.planCodeHint') }}</span>
            </label>
            <label><span>{{ t('insurance.policyDetail.eligibility.eligibleFrom') }}</span><ain-date-input [(ngModel)]="eligibilityDraft.eligibleFrom" /></label>
            <label><span>{{ t('insurance.policyDetail.eligibility.eligibleTo') }}</span><ain-date-input [(ngModel)]="eligibilityDraft.eligibleTo" /></label>
            <label><span>{{ t('insurance.policyDetail.eligibility.waitingPeriodEndsUtc') }}</span><ain-date-input [(ngModel)]="eligibilityDraft.waitingPeriodEndsUtc" /></label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="eligibilityDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="saveEligibilityDialog()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog [header]="t('insurance.policyDetail.endorsement.create')" [modal]="true" [(visible)]="endorsementDialogVisible" [style]="{ width: '720px' }">
          <div class="dialog-grid">
            <label>
              <span>{{ t('insurance.policyDetail.endorsement.type') }}</span>
              <p-select
                [options]="endorsementTypeOptions"
                optionValue="value"
                [(ngModel)]="endorsementDraft.type"
                (ngModelChange)="onEndorsementTypeChanged($event)"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-option>{{ option ? endorsementTypeLabel(option.value) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-option>{{ endorsementTypeLabel(option.value) }}</ng-template>
              </p-select>
            </label>
            <label><span>{{ t('insurance.policyDetail.endorsement.effectiveAt') }}</span><ain-date-input [(ngModel)]="endorsementDraft.effectiveAtUtc" /></label>
            <label><span>{{ t('insurance.policyDetail.endorsement.premiumAdjustment') }}</span><p-inputNumber [(ngModel)]="endorsementDraft.premiumAdjustment" [min]="-1000000000000" /></label>
            <div class="wide">
              <span class="field-label">{{ t('insurance.policyDetail.endorsement.deltaDetails') }}</span>
              <ain-structured-json-form
                [fields]="endorsementDeltaFields()"
                [selectOptionOverrides]="endorsementDeltaSelectOptions()"
                [(payloadJson)]="endorsementDraft.deltaJson" />
            </div>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="endorsementDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveEndorsementDialog()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog [header]="t('insurance.policyDetail.renewal.createOffer')" [modal]="true" [(visible)]="renewalDialogVisible" [style]="{ width: '640px' }">
          <div class="dialog-grid">
            <label><span>{{ t('insurance.policyDetail.renewal.offerValidUntil') }}</span><ain-date-input [(ngModel)]="renewalDraft.offerValidUntilUtc" /></label>
            <label><span>{{ t('insurance.policyDetail.renewal.premiumAmount') }}</span><p-inputNumber [(ngModel)]="renewalDraft.premiumAmount" [min]="0" /></label>
            <label><span>{{ t('insurance.policyDetail.renewal.currencyCode') }}</span><ain-currency-selector [(ngModel)]="renewalDraft.currencyCode" /></label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="renewalDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveRenewalDialog()"></button>
          </ng-template>
        </p-dialog>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .toolbar-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #e5e7eb; }
    .lifecycle-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
    .lines-header h4 { margin: 0; }
    .lines-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .section-gap { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .sub-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .row-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; margin-bottom: 8px; }
    .wide { grid-column: 1 / -1; }
    label { display: grid; gap: 6px; }
    .actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .list-grid { display: grid; gap: 8px; margin-top: 10px; }
    .row { display: flex; gap: 12px; align-items: center; justify-content: space-between; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; }
    .field-hint { font-size: 12px; color: #64748b; }
    .field-label { display: block; color: #475569; font-size: 12px; font-weight: 600; margin-bottom: 6px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    .actions-cell { display: flex; gap: 4px; justify-content: flex-end; white-space: nowrap; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .grid, .sub-grid, .summary-grid, .dialog-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsurancePolicyDetailComponent implements OnInit {
  @ViewChild('draftPreview') private draftPreview?: InsurancePolicyDraftPreviewComponent;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly policy = signal<InsurancePolicyListItem | null>(null);
  readonly activeTab = signal<PolicyTab>('summary');
  readonly isCreateMode = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly products = signal<InsuranceProductListItem[]>([]);
  readonly ratingPlans = signal<InsuranceRatingPlan[]>([]);
  readonly coverageDefinitions = signal<InsuranceCoverageItem[]>([]);
  private resolvedProductVersionId: string | null = null;
  readonly terms = signal<InsurancePolicyTerm[]>([]);
  readonly coverages = signal<InsurancePolicyCoverage[]>([]);
  readonly partyRoles = signal<InsurancePolicyPartyRole[]>([]);
  readonly endorsements = signal<InsuranceEndorsement[]>([]);
  readonly eligibilities = signal<InsuranceMemberEligibility[]>([]);
  readonly renewalOffers = signal<InsuranceRenewalOffer[]>([]);
  readonly auditLogs = signal<InsuranceAuditLogItem[]>([]);
  readonly documents = signal<InsurancePolicyDocument[]>([]);
  readonly sanhabSubmission = signal<InsuranceSanhabSubmission | null>(null);
  readonly premiumSchedule = signal<InsurancePremiumSchedule | null>(null);
  readonly premiumInvoices = signal<InsurancePremiumInvoiceListItem[]>([]);
  readonly billingPermissionDenied = signal(false);
  readonly partyLabels = signal<Record<string, string>>({});
  selectedProductId: string | null = null;
  selectedEditProductId: string | null = null;
  private readonly baseLineOfBusinessOptions = [
    { value: 'motor', label: 'Motor' },
    { value: 'property', label: 'Property' },
    { value: 'life', label: 'Life' },
    { value: 'health', label: 'Health' },
    { value: 'liability', label: 'Liability' },
    { value: 'marine', label: 'Marine' },
    { value: 'credit', label: 'Credit' },
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'travel', label: 'Travel' },
    { value: 'group', label: 'Group' },
    { value: 'reinsurance', label: 'Reinsurance' },
    { value: 'general', label: 'General' }
  ];
  createForm: CreateInsurancePolicyPayload = {
    legalEntityId: '',
    policyNumber: '',
    productName: '',
    lineOfBusiness: '',
    status: 'Draft',
    premiumAmount: 0,
    currencyCode: 'IRR',
    effectiveFromUtc: new Date().toISOString(),
    effectiveToUtc: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
  };
  editForm: UpdateInsurancePolicyPayload = {
    productName: '',
    lineOfBusiness: '',
    status: 'Draft',
    premiumAmount: 0,
    currencyCode: 'IRR',
    effectiveFromUtc: new Date().toISOString(),
    effectiveToUtc: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
  };
  scheduleForm: CreateInsurancePremiumSchedulePayload = {
    legalEntityId: '',
    installmentCount: 1,
    frequency: 'Monthly',
    firstDueDateUtc: null,
    dunningSuspendAfterDays: 30,
    lines: null
  };
  readonly policyStatusOptions = [
    { value: 'Draft', labelKey: 'insurance.policies.status.draft' },
    { value: 'Bound', labelKey: 'insurance.policies.status.bound' },
    { value: 'Issued', labelKey: 'insurance.policies.status.issued' },
    { value: 'InForce', labelKey: 'insurance.policies.status.inForce' },
    { value: 'Suspended', labelKey: 'insurance.policies.status.suspended' },
    { value: 'Cancelled', labelKey: 'insurance.policies.status.cancelled' },
    { value: 'Expired', labelKey: 'insurance.policies.status.expired' },
    { value: 'Renewed', labelKey: 'insurance.policies.status.renewed' }
  ];
  readonly billingFrequencyOptions = [
    { value: 'Single', labelKey: 'insurance.policyDetail.billing.frequency.single' },
    { value: 'Monthly', labelKey: 'insurance.policyDetail.billing.frequency.monthly' },
    { value: 'Quarterly', labelKey: 'insurance.policyDetail.billing.frequency.quarterly' },
    { value: 'Custom', labelKey: 'insurance.policyDetail.billing.frequency.custom' }
  ];
  readonly policyPartyRoleOptions = [
    { value: 'Policyholder', labelKey: 'insurance.policyDetail.parties.roles.policyholder' },
    { value: 'Insured', labelKey: 'insurance.policyDetail.parties.roles.insured' },
    { value: 'Beneficiary', labelKey: 'insurance.policyDetail.parties.roles.beneficiary' },
    { value: 'Payer', labelKey: 'insurance.policyDetail.parties.roles.payer' },
    { value: 'LossPayee', labelKey: 'insurance.policyDetail.parties.roles.lossPayee' }
  ];
  readonly endorsementTypeOptions = [
    { value: 'ChangeCoverage', labelKey: 'insurance.policyDetail.endorsement.types.changeCoverage' },
    { value: 'ChangeRisk', labelKey: 'insurance.policyDetail.endorsement.types.changeRisk' },
    { value: 'ChangeParty', labelKey: 'insurance.policyDetail.endorsement.types.changeParty' },
    { value: 'Cancellation', labelKey: 'insurance.policyDetail.endorsement.types.cancellation' },
    { value: 'Other', labelKey: 'insurance.policyDetail.endorsement.types.other' }
  ];

  readonly coverageDialogVisible = signal(false);
  readonly coverageDialogMode = signal<'create' | 'edit'>('create');
  private coverageDialogIndex = -1;
  coverageDraft: InsurancePolicyCoverage = this.emptyCoverageDraft();

  readonly partyDialogVisible = signal(false);
  readonly partyDialogMode = signal<'create' | 'edit'>('create');
  private partyDialogIndex = -1;
  partyDraft: InsurancePolicyPartyRole = this.emptyPartyDraft();

  readonly eligibilityDialogVisible = signal(false);
  readonly eligibilityDialogMode = signal<'create' | 'edit'>('create');
  private eligibilityDialogIndex = -1;
  eligibilityDraft: InsuranceMemberEligibility = this.emptyEligibilityDraft();

  readonly endorsementDialogVisible = signal(false);
  endorsementDraft: CreateInsuranceEndorsementPayload = this.emptyEndorsementDraft();

  readonly renewalDialogVisible = signal(false);
  renewalDraft: CreateInsuranceRenewalOfferPayload = this.emptyRenewalDraft();

  ngOnInit(): void {
    this.api.listRatingPlans().subscribe({
      next: rows => this.ratingPlans.set(rows),
      error: () => this.ratingPlans.set([])
    });

    this.api.listProducts().subscribe({
      next: rows => {
        this.products.set(rows);
        const current = this.policy();
        if (current) {
          this.syncEditProductSelection(current.productName);
          this.resolveAndLoadCoverageDefinitions(current);
        }
      },
      error: () => this.products.set([])
    });

    const policyId = this.route.snapshot.paramMap.get('policyId');
    const isNewRoute = this.route.snapshot.url.at(-1)?.path === 'new';

    if (isNewRoute) {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    if (!policyId) {
      this.loading.set(false);
      return;
    }

    this.api.getPolicy(policyId).subscribe({
      next: item => {
        this.policy.set(item);
        if (item) {
          this.createForm.legalEntityId = item.id;
          this.editForm = {
            productName: item.productName,
            lineOfBusiness: item.lineOfBusiness,
            status: item.status,
            premiumAmount: item.premiumAmount,
            currencyCode: item.currencyCode,
            effectiveFromUtc: item.effectiveFromUtc,
            effectiveToUtc: item.effectiveToUtc
          };
          this.syncEditProductSelection(item.productName);
          this.resolveAndLoadCoverageDefinitions(item);
          this.scheduleForm.legalEntityId = item.legalEntityId ?? this.createForm.legalEntityId;
          this.scheduleForm.firstDueDateUtc = item.effectiveFromUtc;
          this.loadPolicyLifecycleCollections(item.id);
        }
        this.loading.set(false);
      },
      error: () => {
        this.policy.set(null);
        this.terms.set([]);
        this.coverages.set([]);
        this.partyRoles.set([]);
        this.endorsements.set([]);
        this.renewalOffers.set([]);
        this.auditLogs.set([]);
        this.documents.set([]);
        this.premiumSchedule.set(null);
        this.premiumInvoices.set([]);
        this.loading.set(false);
      }
    });
  }

  private loadPolicyLifecycleCollections(policyId: string): void {
    this.api.listPolicyTerms(policyId).subscribe({
      next: items => this.terms.set(items),
      error: () => this.terms.set([])
    });
    this.api.listPolicyCoverages(policyId).subscribe({
      next: items => this.coverages.set(items),
      error: () => this.coverages.set([])
    });
    this.api.listPolicyPartyRoles(policyId).subscribe({
      next: items => {
        this.partyRoles.set(items);
        this.resolvePartyLabels(items.map(item => item.partyId));
      },
      error: () => this.partyRoles.set([])
    });
    this.api.listPolicyHealthEligibility(policyId).subscribe({
      next: items => {
        this.eligibilities.set(items);
        this.resolvePartyLabels(items.flatMap(item => [item.memberPartyId, item.certificateId]));
      },
      error: () => this.eligibilities.set([])
    });
    this.api.listPolicyEndorsements(policyId).subscribe({
      next: items => this.endorsements.set(items),
      error: () => this.endorsements.set([])
    });
    this.api.listPolicyRenewalOffers(policyId).subscribe({
      next: items => this.renewalOffers.set(items),
      error: () => this.renewalOffers.set([])
    });
    this.api.listPolicyAuditLogs(policyId).subscribe({
      next: items => this.auditLogs.set(items),
      error: () => this.auditLogs.set([])
    });
    this.api.listPolicyDocuments(policyId).subscribe({
      next: items => this.documents.set(items),
      error: () => this.documents.set([])
    });
    this.api.getPolicyPremiumSchedule(policyId).subscribe({
      next: item => {
        this.premiumSchedule.set(item);
        this.billingPermissionDenied.set(false);
      },
      error: err => {
        this.premiumSchedule.set(null);
        this.billingPermissionDenied.set(err?.status === 403);
      }
    });
    this.api.listPremiumInvoices({ status: null }).subscribe({
      next: items => {
        this.premiumInvoices.set(items.filter(item => item.policyId === policyId));
      },
      error: () => this.premiumInvoices.set([])
    });
  }

  pageTitle(): string {
    return this.isCreateMode()
      ? this.t('insurance.policies.form.createTitle')
      : this.t('insurance.policies.form.editTitle');
  }

  statusKey(status: InsurancePolicyListItem['status']): string {
    return `insurance.policies.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  createCanSave(): boolean {
    return !!(
      this.createForm.legalEntityId.trim() &&
      this.createForm.policyNumber.trim() &&
      this.createForm.productName.trim() &&
      this.createForm.lineOfBusiness.trim()
    );
  }

  canSaveEdit(): boolean {
    return !!(this.editForm.productName.trim() && this.editForm.lineOfBusiness.trim());
  }

  saveCreate(): void {
    this.error.set('');
    this.saving.set(true);
    this.api.createPolicy(this.createForm).subscribe({
      next: item => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/policies', item.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  productOptions(): Array<{ label: string; value: string }> {
    return this.products().map(item => ({
      value: item.id,
      label: `${item.code} - ${item.name}`
    }));
  }

  productDisplayLabel(productName: string): string {
    if (!productName?.trim()) {
      return '-';
    }

    const matched = this.products().find(item => item.name === productName);
    if (matched) {
      return `${matched.code} - ${matched.name}`;
    }

    if (productName.startsWith('PV-')) {
      const versionRef = productName.slice(3, 11);
      return this.t('insurance.policies.productVersionRef').replace('{0}', versionRef);
    }

    return productName;
  }

  lobLabel(value: string): string {
    return this.lineOfBusinessOptions().find(item => item.value === value)?.label ?? value;
  }

  lineOfBusinessOptions(): Array<{ label: string; value: string }> {
    const map = new Map<string, { value: string; label: string }>();
    const put = (value: string, label: string): void => {
      const trimmed = value.trim();
      if (!trimmed) {
        return;
      }

      const key = trimmed.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { value: trimmed, label });
      }
    };

    for (const item of this.baseLineOfBusinessOptions) {
      put(item.value, item.label);
    }
    for (const product of this.products()) {
      put(product.lineOfBusiness ?? '', product.lineOfBusiness ?? '');
    }

    const current = this.editForm.lineOfBusiness?.trim();
    if (current) {
      const key = current.toLowerCase();
      const existing = map.get(key);
      map.set(key, { value: current, label: existing?.label ?? current });
    }

    return Array.from(map.values());
  }

  syncEditProductSelection(productName: string): void {
    const matched = this.products().find(item => item.name === productName);
    this.selectedEditProductId = matched?.id ?? null;
  }

  onEditProductChange(productId: string | null): void {
    this.selectedEditProductId = productId;
    if (!productId) {
      return;
    }

    const selected = this.products().find(item => item.id === productId);
    if (!selected) {
      return;
    }

    this.editForm.productName = selected.name;
    if (!this.editForm.lineOfBusiness?.trim()) {
      this.editForm.lineOfBusiness = selected.lineOfBusiness;
    }

    const current = this.policy();
    if (current) {
      this.resolveAndLoadCoverageDefinitions({ ...current, productName: selected.name });
    }
  }

  hasCoverageCatalog(): boolean {
    return this.coverageDefinitions().length > 0;
  }

  coverageCodeOptions(): Array<{ label: string; value: string }> {
    return this.coverageDefinitions().map(item => ({
      value: item.code,
      label: `${item.code} - ${item.name}`
    }));
  }

  onCreateProductChange(productId: string | null): void {
    this.selectedProductId = productId;
    if (!productId) {
      this.createForm.productName = '';
      this.createForm.lineOfBusiness = '';
      return;
    }

    const selected = this.products().find(item => item.id === productId);
    if (!selected) {
      return;
    }

    this.createForm.productName = selected.name;
    this.createForm.lineOfBusiness = selected.lineOfBusiness;
  }

  openDraftPreview(): void {
    const current = this.policy();
    if (!current) {
      return;
    }
    this.api.renderPolicyDraftPreview(current.id, {}).subscribe({
      next: renderResult => this.openDraftPreviewWithRender(current, renderResult),
      error: () => this.openDraftPreviewWithRender(current, null)
    });
  }

  private openDraftPreviewWithRender(
    current: InsurancePolicyListItem,
    renderResult: InsurancePolicyDraftRenderResult | null): void {
    this.draftPreview?.open({
      policy: current,
      coverages: this.coverages(),
      partyRoles: this.partyRoles(),
      terms: this.terms(),
      partyLabels: this.partyLabels(),
      productDisplayName: this.productDisplayLabel(current.productName),
      lineOfBusinessLabel: this.lobLabel(current.lineOfBusiness),
      statusLabel: this.t(this.statusKey(current.status)),
      renderedHtml: renderResult && !renderResult.usedDefaultPreview ? renderResult.renderedHtml : null,
      templateName: renderResult?.templateName ?? null
    });
  }

  saveEdit(): void {
    const current = this.policy();
    if (!current) return;

    this.error.set('');
    this.saving.set(true);
    this.api.updatePolicy(current.id, this.editForm).subscribe({
      next: item => {
        this.policy.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  issue(): void {
    const current = this.policy();
    if (!current || !confirm(this.t('insurance.policyDetail.messages.issueConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.issuePolicy(current.id).subscribe({
      next: item => {
        this.policy.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  suspend(): void {
    const current = this.policy();
    if (!current || !confirm(this.t('insurance.policyDetail.messages.suspendConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.suspendPolicy(current.id, { reason: this.t('insurance.policyDetail.messages.manualActionReason') }).subscribe({
      next: item => {
        this.policy.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  reinstate(): void {
    const current = this.policy();
    if (!current || !confirm(this.t('insurance.policyDetail.messages.reinstateConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.reinstatePolicy(current.id, { reason: this.t('insurance.policyDetail.messages.manualActionReason') }).subscribe({
      next: item => {
        this.policy.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  cancel(): void {
    const current = this.policy();
    if (!current || !confirm(this.t('insurance.policyDetail.messages.cancelConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.cancelPolicy(current.id, { reason: this.t('insurance.policyDetail.messages.manualActionReason') }).subscribe({
      next: item => {
        this.policy.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  expire(): void {
    const current = this.policy();
    if (!current || !confirm(this.t('insurance.policyDetail.messages.expireConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.expirePolicy(current.id).subscribe({
      next: item => {
        this.policy.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  endorsementTypeLabel(type: string): string {
    const option = this.endorsementTypeOptions.find(item => item.value === type);
    return option ? this.t(option.labelKey) : type;
  }

  endorsementDeltaFields(): StructuredJsonFieldDef[] {
    const fields = getEndorsementDeltaFields(this.endorsementDraft.type);
    if (this.endorsementDraft.type === 'ChangeCoverage' && !this.hasCoverageCatalog()) {
      return fields.map(field => field.key === 'coverageCode'
        ? { ...field, type: 'string' as const }
        : field);
    }
    return fields;
  }

  endorsementDeltaSelectOptions(): Record<string, Array<{ label: string; value: string }>> {
    if (this.endorsementDraft.type !== 'ChangeCoverage') {
      return {};
    }
    return {
      coverageCode: this.coverageCodeOptions()
    };
  }

  onEndorsementTypeChanged(type: string): void {
    this.endorsementDraft.type = type;
    this.endorsementDraft.deltaJson = serializeStructuredJsonPayload(
      defaultStructuredJsonModel(this.endorsementDeltaFields())
    );
  }

  healthPlanCodeOptions(): Array<{ label: string; value: string }> {
    const versionId = this.resolvedProductVersionId;
    const plans = this.ratingPlans().filter(plan => !versionId || plan.productVersionId === versionId);
    const options = plans.map(plan => ({ value: plan.code, label: `${plan.code} - ${plan.name}` }));
    const codes = new Set(options.map(option => option.value));
    if (!codes.has('STANDARD')) {
      options.unshift({ value: 'STANDARD', label: 'STANDARD' });
      codes.add('STANDARD');
    }
    for (const item of this.eligibilities()) {
      if (item.planCode && !codes.has(item.planCode)) {
        options.push({ value: item.planCode, label: item.planCode });
        codes.add(item.planCode);
      }
    }
    return options;
  }

  openCoverageCreate(): void {
    this.coverageDialogMode.set('create');
    this.coverageDialogIndex = -1;
    this.coverageDraft = this.emptyCoverageDraft();
    const defaultCode = this.coverageDefinitions()[0]?.code ?? '';
    if (defaultCode) {
      this.coverageDraft.coverageCode = defaultCode;
      this.onCoverageDraftCodeChanged(defaultCode);
    }
    this.coverageDialogVisible.set(true);
  }

  openCoverageEdit(index: number): void {
    const row = this.coverages()[index];
    if (!row) {
      return;
    }
    this.coverageDialogMode.set('edit');
    this.coverageDialogIndex = index;
    this.coverageDraft = { ...row };
    this.coverageDialogVisible.set(true);
  }

  removeCoverage(index: number): void {
    const next = [...this.coverages()];
    next.splice(index, 1);
    this.coverages.set(next);
  }

  onCoverageDraftCodeChanged(code: string | null): void {
    this.coverageDraft.coverageCode = code ?? '';
    const coverage = this.coverageDefinitions().find(row => row.code === this.coverageDraft.coverageCode);
    if (!coverage) {
      return;
    }
    this.coverageDraft.coverageName = coverage.name;
    if (!this.coverageDraft.sumInsured) {
      this.coverageDraft.sumInsured = coverage.defaultSumInsured ?? coverage.minSumInsured ?? 0;
    }
  }

  saveCoverageDialog(): void {
    if (!this.coverageDraft.coverageCode.trim()) {
      return;
    }
    const row: InsurancePolicyCoverage = {
      ...this.coverageDraft,
      coverageName: this.coverageDraft.coverageName || this.coverageDraft.coverageCode,
      currencyCode: this.coverageDraft.currencyCode || this.policy()?.currencyCode || 'IRR'
    };
    const next = [...this.coverages()];
    if (this.coverageDialogMode() === 'create') {
      next.push(row);
    } else if (this.coverageDialogIndex >= 0) {
      next[this.coverageDialogIndex] = row;
    }
    this.coverages.set(next);
    this.coverageDialogVisible.set(false);
  }

  coverageDialogTitle(): string {
    return this.coverageDialogMode() === 'create' ? this.t('common.add') : this.t('common.edit');
  }

  openPartyCreate(): void {
    this.partyDialogMode.set('create');
    this.partyDialogIndex = -1;
    this.partyDraft = this.emptyPartyDraft();
    this.partyDialogVisible.set(true);
  }

  openPartyEdit(index: number): void {
    const row = this.partyRoles()[index];
    if (!row) {
      return;
    }
    this.partyDialogMode.set('edit');
    this.partyDialogIndex = index;
    this.partyDraft = { ...row };
    this.partyDialogVisible.set(true);
  }

  removePartyRole(index: number): void {
    const next = [...this.partyRoles()];
    next.splice(index, 1);
    this.partyRoles.set(next);
  }

  savePartyDialog(): void {
    if (!this.partyDraft.partyId.trim()) {
      return;
    }
    const row: InsurancePolicyPartyRole = {
      ...this.partyDraft,
      role: this.partyDraft.role || 'Insured'
    };
    const next = [...this.partyRoles()];
    if (this.partyDialogMode() === 'create') {
      next.push(row);
    } else if (this.partyDialogIndex >= 0) {
      next[this.partyDialogIndex] = row;
    }
    this.partyRoles.set(next);
    this.resolvePartyLabels([row.partyId]);
    this.partyDialogVisible.set(false);
  }

  partyDialogTitle(): string {
    return this.partyDialogMode() === 'create' ? this.t('common.add') : this.t('common.edit');
  }

  openEligibilityCreate(): void {
    this.eligibilityDialogMode.set('create');
    this.eligibilityDialogIndex = -1;
    this.eligibilityDraft = this.emptyEligibilityDraft();
    const defaultPlan = this.healthPlanCodeOptions()[0]?.value ?? 'STANDARD';
    this.eligibilityDraft.planCode = defaultPlan;
    this.eligibilityDialogVisible.set(true);
  }

  openEligibilityEdit(index: number): void {
    const row = this.eligibilities()[index];
    if (!row) {
      return;
    }
    this.eligibilityDialogMode.set('edit');
    this.eligibilityDialogIndex = index;
    this.eligibilityDraft = { ...row };
    this.eligibilityDialogVisible.set(true);
  }

  removeEligibility(index: number): void {
    const next = [...this.eligibilities()];
    next.splice(index, 1);
    this.eligibilities.set(next);
  }

  saveEligibilityDialog(): void {
    if (!this.eligibilityDraft.memberPartyId.trim()) {
      return;
    }
    const row: InsuranceMemberEligibility = {
      ...this.eligibilityDraft,
      planCode: this.eligibilityDraft.planCode || 'STANDARD'
    };
    const next = [...this.eligibilities()];
    if (this.eligibilityDialogMode() === 'create') {
      next.push(row);
    } else if (this.eligibilityDialogIndex >= 0) {
      next[this.eligibilityDialogIndex] = row;
    }
    this.eligibilities.set(next);
    this.resolvePartyLabels([row.memberPartyId, row.certificateId]);
    this.eligibilityDialogVisible.set(false);
  }

  eligibilityDialogTitle(): string {
    return this.eligibilityDialogMode() === 'create' ? this.t('common.add') : this.t('common.edit');
  }

  openEndorsementCreate(): void {
    this.endorsementDraft = this.emptyEndorsementDraft();
    this.endorsementDraft.deltaJson = serializeStructuredJsonPayload(
      defaultStructuredJsonModel(getEndorsementDeltaFields(this.endorsementDraft.type))
    );
    this.endorsementDialogVisible.set(true);
  }

  saveEndorsementDialog(): void {
    const current = this.policy();
    if (!current) {
      return;
    }
    this.saving.set(true);
    this.api.createPolicyEndorsement(current.id, this.endorsementDraft).subscribe({
      next: item => {
        this.endorsements.set([item, ...this.endorsements()]);
        this.endorsementDialogVisible.set(false);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  openRenewalCreate(): void {
    const current = this.policy();
    this.renewalDraft = this.emptyRenewalDraft();
    if (current) {
      this.renewalDraft.premiumAmount = current.premiumAmount;
      this.renewalDraft.currencyCode = current.currencyCode;
    }
    this.renewalDialogVisible.set(true);
  }

  saveRenewalDialog(): void {
    const current = this.policy();
    if (!current) {
      return;
    }
    this.saving.set(true);
    this.api.createPolicyRenewalOffer(current.id, this.renewalDraft).subscribe({
      next: item => {
        this.renewalOffers.set([item, ...this.renewalOffers()]);
        this.renewalDialogVisible.set(false);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  private emptyCoverageDraft(): InsurancePolicyCoverage {
    return {
      id: '',
      policyId: this.policy()?.id ?? '',
      coverageCode: '',
      coverageName: '',
      sumInsured: 0,
      premiumAmount: 0,
      deductibleAmount: null,
      currencyCode: this.policy()?.currencyCode ?? 'IRR'
    };
  }

  private emptyPartyDraft(): InsurancePolicyPartyRole {
    return {
      id: '',
      policyId: this.policy()?.id ?? '',
      partyId: '',
      role: 'Insured'
    };
  }

  private emptyEligibilityDraft(): InsuranceMemberEligibility {
    const current = this.policy();
    return {
      id: '',
      policyId: current?.id ?? '',
      certificateId: null,
      memberPartyId: '',
      eligibleFrom: current?.effectiveFromUtc ?? new Date().toISOString(),
      eligibleTo: current?.effectiveToUtc ?? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      planCode: 'STANDARD',
      waitingPeriodEndsUtc: null
    };
  }

  private emptyEndorsementDraft(): CreateInsuranceEndorsementPayload {
    return {
      type: 'ChangeCoverage',
      effectiveAtUtc: new Date().toISOString(),
      deltaJson: '{}',
      premiumAdjustment: 0
    };
  }

  private emptyRenewalDraft(): CreateInsuranceRenewalOfferPayload {
    return {
      offerValidUntilUtc: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      newProductVersionId: null,
      premiumBreakdownId: null,
      premiumAmount: 0,
      currencyCode: 'IRR'
    };
  }

  private resolveAndLoadCoverageDefinitions(policy: InsurancePolicyListItem): void {
    const versionIdFromName = this.parseProductVersionId(policy.productName);
    if (versionIdFromName) {
      this.resolvedProductVersionId = versionIdFromName;
      this.resolveProductByVersion(versionIdFromName, productId => {
        if (!productId) {
          this.coverageDefinitions.set([]);
          return;
        }
        this.loadCoverageDefinitions(productId, versionIdFromName);
      });
      return;
    }

    const productId = this.selectedEditProductId
      ?? this.products().find(item => item.name === policy.productName)?.id
      ?? null;
    if (!productId) {
      this.coverageDefinitions.set([]);
      return;
    }

    this.api.getProduct(productId).subscribe({
      next: detail => {
        if (!detail?.versions.length) {
          this.coverageDefinitions.set([]);
          return;
        }
        const version = detail.versions.find(item => item.status === 'Published') ?? detail.versions[0];
        this.resolvedProductVersionId = version.id;
        this.loadCoverageDefinitions(productId, version.id);
      },
      error: () => this.coverageDefinitions.set([])
    });
  }

  private parseProductVersionId(productName: string): string | null {
    if (!productName.startsWith('PV-')) {
      return null;
    }
    const versionId = productName.slice(3).trim();
    return /^[0-9a-fA-F-]{36}$/.test(versionId) ? versionId : null;
  }

  private resolveProductByVersion(versionId: string, onResolved: (productId: string | null) => void): void {
    const products = this.products();
    if (products.length === 0) {
      onResolved(null);
      return;
    }

    let index = 0;
    const tryNext = () => {
      if (index >= products.length) {
        onResolved(null);
        return;
      }
      const productId = products[index++].id;
      this.api.getProduct(productId).subscribe({
        next: detail => {
          if (detail?.versions.some(item => item.id === versionId)) {
            onResolved(productId);
          } else {
            tryNext();
          }
        },
        error: () => tryNext()
      });
    };
    tryNext();
  }

  private loadCoverageDefinitions(productId: string, versionId: string): void {
    this.api.listVersionCoverages(productId, versionId).subscribe({
      next: rows => this.coverageDefinitions.set(rows),
      error: () => this.coverageDefinitions.set([])
    });
  }

  saveCoverages(): void {
    const current = this.policy();
    if (!current) {
      return;
    }
    const payload: UpsertInsurancePolicyCoveragesPayload = {
      items: this.coverages()
        .filter(item => item.coverageCode.trim().length > 0)
        .map(item => ({
          coverageCode: item.coverageCode,
          coverageName: item.coverageName || item.coverageCode,
          sumInsured: item.sumInsured ?? 0,
          premiumAmount: item.premiumAmount ?? 0,
          deductibleAmount: item.deductibleAmount,
          currencyCode: item.currencyCode || 'IRR'
        }))
    };
    this.saving.set(true);
    this.api.upsertPolicyCoverages(current.id, payload).subscribe({
      next: items => {
        this.coverages.set(items);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  partyRoleOptionsFor(currentRole: string): Array<{ value: string; labelKey: string }> {
    const normalized = currentRole?.trim();
    if (!normalized || this.policyPartyRoleOptions.some(item => item.value === normalized)) {
      return this.policyPartyRoleOptions;
    }

    return [
      ...this.policyPartyRoleOptions,
      { value: normalized, labelKey: '' }
    ];
  }

  partyRoleLabel(role: string): string {
    const option = this.policyPartyRoleOptions.find(item => item.value === role);
    return option ? this.t(option.labelKey) : role;
  }

  partyDisplayLabel(partyId: string | null | undefined): string {
    const normalized = partyId?.trim();
    if (!normalized) {
      return '-';
    }
    return this.partyLabels()[normalized] ?? normalized;
  }

  private resolvePartyLabels(partyIds: Array<string | null | undefined>): void {
    const known = this.partyLabels();
    const pending = [...new Set(
      partyIds
        .map(id => id?.trim())
        .filter((id): id is string => !!id && !known[id])
    )];
    for (const partyId of pending) {
      this.financeApi.getPlatformParty(partyId).subscribe({
        next: party => {
          const label = party.displayName?.trim() || party.legalName?.trim() || party.id;
          this.partyLabels.update(labels => ({ ...labels, [party.id]: label }));
        },
        error: () => {}
      });
    }
  }

  saveEligibility(): void {
    const current = this.policy();
    if (!current) {
      return;
    }
    const payload: UpsertInsuranceMemberEligibilityPayload = {
      items: this.eligibilities()
        .filter(item => !!item.memberPartyId)
        .map(item => ({
          memberPartyId: item.memberPartyId,
          certificateId: item.certificateId,
          eligibleFrom: item.eligibleFrom,
          eligibleTo: item.eligibleTo,
          planCode: item.planCode || 'STANDARD',
          waitingPeriodEndsUtc: item.waitingPeriodEndsUtc
        }))
    };
    this.saving.set(true);
    this.api.upsertPolicyHealthEligibility(current.id, payload).subscribe({
      next: items => {
        this.eligibilities.set(items);
        this.resolvePartyLabels(items.flatMap(item => [item.memberPartyId, item.certificateId]));
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  savePartyRoles(): void {
    const current = this.policy();
    if (!current) {
      return;
    }
    const payload: UpsertInsurancePolicyPartyRolesPayload = {
      items: this.partyRoles()
        .filter(item => !!item.partyId)
        .map(item => ({
          partyId: item.partyId,
          role: item.role || 'Insured'
        }))
    };
    this.saving.set(true);
    this.api.upsertPolicyPartyRoles(current.id, payload).subscribe({
      next: items => {
        this.partyRoles.set(items);
        this.resolvePartyLabels(items.map(item => item.partyId));
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  applyEndorsement(endorsementId: string): void {
    const current = this.policy();
    if (!current || !confirm(this.t('insurance.policyDetail.messages.applyEndorsementConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.applyPolicyEndorsement(current.id, endorsementId, this.t('insurance.policyDetail.messages.manualActionReason')).subscribe({
      next: item => {
        this.endorsements.set(this.endorsements().map(x => x.id === item.id ? item : x));
        this.api.getPolicy(current.id).subscribe(p => {
          if (p) {
            this.policy.set(p);
          }
        });
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  renewFromOffer(offerId: string): void {
    const current = this.policy();
    if (!current || !confirm(this.t('insurance.policyDetail.messages.renewConfirm'))) {
      return;
    }
    const payload: RenewInsurancePolicyPayload = {
      renewalOfferId: offerId,
      effectiveFromUtc: current.effectiveToUtc,
      effectiveToUtc: new Date(new Date(current.effectiveToUtc).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString()
    };
    this.saving.set(true);
    this.api.renewPolicy(current.id, payload).subscribe({
      next: item => {
        void this.router.navigate(['/insurance/policies', item.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  createPremiumSchedule(): void {
    const current = this.policy();
    if (!current) {
      return;
    }
    this.saving.set(true);
    this.error.set('');
    this.api.createPolicyPremiumSchedule(current.id, this.scheduleForm).subscribe({
      next: schedule => {
        this.premiumSchedule.set(schedule);
        this.api.listPremiumInvoices({ status: null }).subscribe(items => {
          this.premiumInvoices.set(items.filter(item => item.policyId === current.id));
        });
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  runEarning(): void {
    const current = this.policy();
    if (!current) {
      return;
    }
    if (!confirm(this.t('insurance.policyDetail.billing.messages.runEarningConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.error.set('');
    const payload: RunInsurancePremiumEarningPayload = {
      period: new Date().toISOString().slice(0, 7),
      policyId: current.id
    };
    this.api.runPremiumEarningRun(payload).subscribe({
      next: () => {
        this.api.getPolicy(current.id).subscribe(item => {
          if (item) {
            this.policy.set(item);
          }
        });
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  openPremiumInvoice(invoiceId: string): void {
    void this.router.navigate(['/insurance/premium-invoices', invoiceId]);
  }

  syncPolicyToSanhab(): void {
    const current = this.policy();
    if (!current || !confirm(this.t('insurance.regulatory.console.messages.syncConfirm'))) {
      return;
    }

    this.saving.set(true);
    this.api.syncSanhabPolicy(current.id, { force: false, payloadJson: null }).subscribe({
      next: submission => {
        this.sanhabSubmission.set(submission);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
